import { mapErrorToUserMessage } from '@repo/core';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
/**
 * ─── Settings Page ───────────────────────────────────────────
 * Central settings hub for cooperative configuration.
 */

import { useEffect, useState } from 'react'
import { Card, CardHeader, Badge, Button, Input, Modal } from '@repo/ui-core'
import type { Frequency } from '@repo/entity-loan'
import { collectionGroupRepo, expenseCategoryRepo, employeeRepo, loanProductRepo } from '../../lib/db'
import { useAllFlags, useToggleFlag } from '../../context/FeatureFlagContext'
import { Plus, Save, Layers, Tag, Users, Briefcase, Settings2, ChevronLeft, ChevronRight, Blocks, Palette, Monitor, Tablet, Smartphone, FileText, Check, ExternalLink } from 'lucide-react'
import { useUITheme } from '../../context/UIThemeContext'
import { Link } from '@tanstack/react-router'

type SettingsTab = 'ui-theme' | 'collection-groups' | 'expense-categories' | 'employees' | 'loan-products' | 'modules'

export function SettingsPage() {
  const [activeTab, setActiveTab] = useState<SettingsTab>('ui-theme')

  const tabs = [
    { id: 'ui-theme' as SettingsTab, label: 'UI / UX Design', icon: <Palette className="h-4 w-4 text-amber-600" /> },
    { id: 'modules' as SettingsTab, label: 'Modules', icon: <Blocks className="h-4 w-4" /> },
    { id: 'collection-groups' as SettingsTab, label: 'Collection Groups', icon: <Layers className="h-4 w-4" /> },
    { id: 'expense-categories' as SettingsTab, label: 'Expense Categories', icon: <Tag className="h-4 w-4" /> },
    { id: 'employees' as SettingsTab, label: 'Employees', icon: <Users className="h-4 w-4" /> },
    { id: 'loan-products' as SettingsTab, label: 'Loan Products', icon: <Briefcase className="h-4 w-4" /> },
  ]

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">Settings</h1>
        <p className="mt-1 text-sm text-gray-500">Configure cooperative business rules and reference data</p>
      </div>

      <div className="flex gap-6">
        <div className="w-48 shrink-0 space-y-1">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${
                activeTab === tab.id ? 'bg-green-50 text-green-700 font-medium' : 'text-gray-600 hover:bg-gray-50'
              }`}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>

        <div className="flex-1 min-w-0">
          {activeTab === 'ui-theme' && <UIThemeSettings />}
          {activeTab === 'collection-groups' && <CollectionGroupsSettings />}
          {activeTab === 'expense-categories' && <ExpenseCategoriesSettings />}
          {activeTab === 'employees' && <EmployeesSettings />}
          {activeTab === 'loan-products' && <LoanProductsSettings />}
          {activeTab === 'modules' && <ModuleSettings />}
        </div>
      </div>
    </div>
  )
}

function CollectionGroupsSettings() {
  const [items, setItems] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({ name: '', collectionDay: 0, collectorId: '' })

  useEffect(() => {
    collectionGroupRepo.findMany({ page: 1, pageSize: 100 }).then(r => {
      if ('items' in r) setItems(r.items)
    }).finally(() => setLoading(false))
  }, [])

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault(); setSaving(true)
    try {
      await collectionGroupRepo.create({ ...form, tenantId: 'default', createdBy: 'admin', updatedBy: 'admin' })
      setShowModal(false); setForm({ name: '', collectionDay: 0, collectorId: '' })
      const r = await collectionGroupRepo.findMany({ page: 1, pageSize: 100 })
      if ('items' in r) setItems(r.items)
    } catch (error) { console.error(error); alert(mapErrorToUserMessage(error).userMessage) }
    finally { setSaving(false) }
  }

  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

  return (
    <Card>
      <CardHeader title="Collection Groups" description="Define collector routes and schedules"
        action={<Button onClick={() => setShowModal(true)} icon={<Plus className="h-4 w-4" />}>Add Group</Button>} />
      {loading ? <p className="py-8 text-center text-gray-400">Loading...</p> : items.length === 0 ? (
        <p className="py-8 text-center text-gray-400">No collection groups defined</p>
      ) : (
        <div className="space-y-2">
          {items.map((g: any) => (
            <div key={g.id} className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3">
              <div><p className="font-medium text-gray-900">{g.name}</p><p className="text-sm text-gray-500">{dayNames[g.collectionDay] ?? 'Unknown'} · {g.collectorId || 'No collector assigned'}</p></div>
              <Badge color={g.is_active !== false ? 'green' : 'gray'}>{g.is_active !== false ? 'Active' : 'Inactive'}</Badge>
            </div>
          ))}
        </div>
      )}
      <Modal open={showModal} onClose={() => setShowModal(false)} title="Add Collection Group" size="sm"
        footer={<><Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button><Button onClick={handleCreate} loading={saving} icon={<Save className="h-4 w-4" />}>Save</Button></>}>
        <form onSubmit={handleCreate} className="space-y-3">
          <Input label="Group Name" required value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} />
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Collection Day</label>
            <select value={form.collectionDay} onChange={(e) => setForm(f => ({ ...f, collectionDay: parseInt(e.target.value) }))}
              className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm">
              {dayNames.map((d, i) => <option key={i} value={i}>{d}</option>)}
            </select></div>
          <Input label="Collector ID" value={form.collectorId} onChange={(e) => setForm(f => ({ ...f, collectorId: e.target.value }))} />
        </form>
      </Modal>
    </Card>
  )
}

function ExpenseCategoriesSettings() {
  const [items, setItems] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({ name: '' })

  useEffect(() => {
    expenseCategoryRepo.findMany({ page: 1, pageSize: 100 }).then(r => {
      if ('items' in r) setItems(r.items)
    }).finally(() => setLoading(false))
  }, [])

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault(); setSaving(true)
    try {
      await expenseCategoryRepo.create({ name: form.name, tenantId: 'default', createdBy: 'admin', updatedBy: 'admin' })
      setShowModal(false); setForm({ name: '' })
      const r = await expenseCategoryRepo.findMany({ page: 1, pageSize: 100 })
      if ('items' in r) setItems(r.items)
    } catch (error) { console.error(error); alert(mapErrorToUserMessage(error).userMessage) }
    finally { setSaving(false) }
  }

  return (
    <Card>
      <CardHeader title="Expense Categories" action={<Button onClick={() => setShowModal(true)} icon={<Plus className="h-4 w-4" />}>Add Category</Button>} />
      {loading ? <p className="py-8 text-center text-gray-400">Loading...</p> : items.length === 0 ? (
        <p className="py-8 text-center text-gray-400">No categories defined</p>
      ) : (
        <div className="flex flex-wrap gap-2">{items.map((c: any) => (
          <span key={c.id} className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700">{c.name}</span>
        ))}</div>
      )}
      <Modal open={showModal} onClose={() => setShowModal(false)} title="Add Expense Category" size="sm"
        footer={<><Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button><Button onClick={handleCreate} loading={saving} icon={<Save className="h-4 w-4" />}>Save</Button></>}>
        <Input label="Category Name" required value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} />
      </Modal>
    </Card>
  )
}

function EmployeesSettings() {
  const [items, setItems] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({ name: '', role: 'staff', baseSalary: 0 })

  useEffect(() => {
    employeeRepo.findMany({ page: 1, pageSize: 100 }).then(r => {
      if ('items' in r) setItems(r.items)
    }).finally(() => setLoading(false))
  }, [])

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault(); setSaving(true)
    try {
      await employeeRepo.create({ ...form, tenantId: 'default', createdBy: 'admin', updatedBy: 'admin' })
      setShowModal(false); setForm({ name: '', role: 'staff', baseSalary: 0 })
      const r = await employeeRepo.findMany({ page: 1, pageSize: 100 })
      if ('items' in r) setItems(r.items)
    } catch (error) { console.error(error); alert(mapErrorToUserMessage(error).userMessage) }
    finally { setSaving(false) }
  }

  return (
    <Card>
      <CardHeader title="Employees" description="Staff and payroll management"
        action={<Button onClick={() => setShowModal(true)} icon={<Plus className="h-4 w-4" />}>Add Employee</Button>} />
      {loading ? <p className="py-8 text-center text-gray-400">Loading...</p> : items.length === 0 ? (
        <p className="py-8 text-center text-gray-400">No employees yet</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200"><thead className="bg-gray-50"><tr>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Name</th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Role</th>
            <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">Base Salary</th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Status</th>
          </tr></thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {items.map((e: any) => (
              <tr key={e.id}><td className="px-4 py-3 text-sm text-gray-900">{e.name}</td>
                <td className="px-4 py-3 text-sm text-gray-500 capitalize">{e.role}</td>
                <td className="px-4 py-3 text-right text-sm">₱{(e.baseSalary ?? 0).toLocaleString()}</td>
                <td className="px-4 py-3"><Badge color={e.is_active !== false ? 'green' : 'gray'}>{e.is_active !== false ? 'Active' : 'Inactive'}</Badge></td>
              </tr>
            ))}
          </tbody></table>
        </div>
      )}
      <Modal open={showModal} onClose={() => setShowModal(false)} title="Add Employee" size="sm"
        footer={<><Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button><Button onClick={handleCreate} loading={saving} icon={<Save className="h-4 w-4" />}>Save</Button></>}>
        <form onSubmit={handleCreate} className="space-y-3">
          <Input label="Full Name" required value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} />
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
            <select value={form.role} onChange={(e) => setForm(f => ({ ...f, role: e.target.value }))}
              className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm shadow-sm">
              <option value="staff">Staff</option> <option value="manager">Manager</option>
              <option value="officer">Officer</option> <option value="collector">Collector</option>
            </select></div>
          <Input label="Base Salary (₱)" type="number" value={String(form.baseSalary)} onChange={(e) => setForm(f => ({ ...f, baseSalary: Number(e.target.value) }))} />
        </form>
      </Modal>
    </Card>
  )
}

function LoanProductsSettings() {
  const [items, setItems] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({ productType: '', label: '', defaultRatePercent: 12, defaultTerm: 12, defaultFrequency: 'monthly' as Frequency })

  useEffect(() => {
    loanProductRepo.findMany({ page: 1, pageSize: 100 }).then((r: any) => {
      if ('items' in r) setItems(r.items)
    }).finally(() => setLoading(false))
  }, [])

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault(); setSaving(true)
    try {
      await loanProductRepo.create({ ...form, defaultTermUnit: 'months', is_active: true, tenantId: 'default' })
      setShowModal(false); setForm({ productType: '', label: '', defaultRatePercent: 12, defaultTerm: 12, defaultFrequency: 'monthly' as Frequency })
      const r = await loanProductRepo.findMany({ page: 1, pageSize: 100 })
      if ('items' in r) setItems(r.items)
    } catch (error) { console.error(error); alert(mapErrorToUserMessage(error).userMessage) }
    finally { setSaving(false) }
  }

  return (
    <Card>
      <CardHeader title="Loan Products" action={<Button onClick={() => setShowModal(true)} icon={<Plus className="h-4 w-4" />}>Add Product</Button>} />
      {loading ? <p className="py-8 text-center text-gray-400">Loading...</p> : items.length === 0 ? (
        <p className="py-8 text-center text-gray-400">No loan products defined</p>
      ) : (
        <div className="space-y-2">{items.map((p: any) => (
          <div key={p.id} className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3">
            <div><p className="font-medium text-gray-900">{p.label}</p><p className="text-sm text-gray-500">{p.productType} · {p.defaultRatePercent}% · {p.defaultTerm} months</p></div>
            <Badge color={p.is_active !== false ? 'green' : 'gray'}>{p.is_active !== false ? 'Active' : 'Inactive'}</Badge>
          </div>
        ))}</div>
      )}
      <Modal open={showModal} onClose={() => setShowModal(false)} title="Add Loan Product" size="sm"
        footer={<><Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button><Button onClick={handleCreate} loading={saving} icon={<Save className="h-4 w-4" />}>Save</Button></>}>
        <form className="space-y-3">
          <Input label="Product Type" required value={form.productType} onChange={(e) => setForm(f => ({ ...f, productType: e.target.value }))} />
          <Input label="Label" required value={form.label} onChange={(e) => setForm(f => ({ ...f, label: e.target.value }))} />
          <Input label="Default Rate (%)" type="number" value={String(form.defaultRatePercent)} onChange={(e) => setForm(f => ({ ...f, defaultRatePercent: Number(e.target.value) }))} />
          <Input label="Default Term (months)" type="number" value={String(form.defaultTerm)} onChange={(e) => setForm(f => ({ ...f, defaultTerm: Number(e.target.value) }))} />
        </form>
      </Modal>
    </Card>
  )
}

// ─── Modules Toggle ───────────────────────────────────────────

interface ModuleDef {
  flagKey: string
  label: string
  description: string
  icon: string
  category: 'core' | 'vertical'
}

const MODULES: ModuleDef[] = [
  {
    flagKey: 'module.cooperative',
    label: 'Cooperative ERP',
    description: 'Members, lending, savings, share capital, accounting & collections',
    icon: 'Building2',
    category: 'core',
  },
  {
    flagKey: 'module.multi-branch',
    label: 'Multi-Branch',
    description: 'Branch entities, per-branch filtering and reports',
    icon: 'GitBranch',
    category: 'core',
  },
  {
    flagKey: 'module.changelog',
    label: 'Changelog / Roadmap',
    description: 'Release version tracking & feature roadmap',
    icon: 'Clock',
    category: 'core',
  },
  {
    flagKey: 'module.laundry',
    label: 'Laundry Shop',
    description: 'Multi-branch laundry: customers, orders, services, payments, inventory',
    icon: 'Shirt',
    category: 'vertical',
  },
  {
    flagKey: 'module.clinic',
    label: 'Clinic',
    description: 'Patients, doctors, appointments, consultation records, billing',
    icon: 'HeartPulse',
    category: 'vertical',
  },
  {
    flagKey: 'module.driving-school',
    label: 'Driving School',
    description: 'Students, instructors, courses, enrollments, schedules, payments, vehicles',
    icon: 'Car',
    category: 'vertical',
  },
  {
    flagKey: 'module.fastfood',
    label: 'Fast Food',
    description: 'Menu, POS orders, inventory tracking, daily sales summary',
    icon: 'UtensilsCrossed',
    category: 'vertical',
  },
  {
    flagKey: 'module.water-station',
    label: 'Water Station',
    description: 'Water delivery customers, deliveries, containers & payments',
    icon: 'Droplets',
    category: 'vertical',
  },
]

function ModuleSettings() {
  const allFlags = useAllFlags()
  const toggle = useToggleFlag()

  const flagsByKey = new Map(allFlags.map(f => [f.key, f]))

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="Module Toggles"
          description="Turn business domains on or off. Changes apply immediately to the sidebar and routes."
        />

        {/* Core Modules */}
        <div className="mb-6">
          <p className="mb-3 px-1 text-xs font-semibold uppercase tracking-wider text-gray-400">
            Platform Features
          </p>
          <div className="space-y-1">
            {MODULES.filter(m => m.category === 'core').map(mod => {
              const flag = flagsByKey.get(mod.flagKey)
              const enabled = flag?.resolved ?? flag?.enabled ?? false
              return (
                <div
                  key={mod.flagKey}
                  className="flex items-center justify-between rounded-lg px-4 py-3 transition-colors hover:bg-gray-50"
                >
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{mod.label}</p>
                    <p className="text-xs text-gray-500">{mod.description}</p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={enabled}
                    aria-label={`Toggle ${mod.label}`}
                    onClick={() => toggle(mod.flagKey, !enabled)}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${
                      enabled ? 'bg-green-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
                        enabled ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              )
            })}
          </div>
        </div>

        {/* Vertical Modules */}
        <div>
          <p className="mb-3 px-1 text-xs font-semibold uppercase tracking-wider text-gray-400">
            Business Line Modules
          </p>
          <div className="space-y-1">
            {MODULES.filter(m => m.category === 'vertical').map(mod => {
              const flag = flagsByKey.get(mod.flagKey)
              const enabled = flag?.resolved ?? flag?.enabled ?? false
              return (
                <div
                  key={mod.flagKey}
                  className="flex items-center justify-between rounded-lg px-4 py-3 transition-colors hover:bg-gray-50"
                >
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{mod.label}</p>
                    <p className="text-xs text-gray-500">{mod.description}</p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={enabled}
                    aria-label={`Toggle ${mod.label}`}
                    onClick={() => toggle(mod.flagKey, !enabled)}
                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 ${
                      enabled ? 'bg-green-600' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ${
                        enabled ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      </Card>

      {/* Persistence note */}
      <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
        <p className="text-sm text-amber-800">
          <strong>Note:</strong> Module toggles are stored in memory and reset on page reload.
          In production, they persist per tenant via <code className="rounded bg-amber-100 px-1 text-xs">tenant_metadata</code>.
        </p>
      </div>
    </div>
  )
}

function UIThemeSettings() {
  const { theme, setTheme, deviceView, setDeviceView } = useUITheme()

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          title="UI / UX Theme & Layout Selection"
          description="Choose your preferred user interface design theme. You can switch between the Default Cooperative ERP UI and the Cebuano/Bisaya Manila Paper Loan Ledger UI (cloned pixel-by-pixel for PC, Tablet, and Mobile Phone)."
        />
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 p-6 pt-0">
          {/* Default Theme Card */}
          <div
            onClick={() => setTheme('default')}
            className={`relative flex flex-col justify-between rounded-2xl border-2 p-5 cursor-pointer transition-all ${
              theme === 'default'
                ? 'border-green-600 bg-green-50/40 shadow-md ring-2 ring-green-600/20'
                : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
            }`}
          >
            <div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-bold text-gray-900">Default Cooperative ERP UI</span>
                {theme === 'default' && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-green-600 px-2.5 py-0.5 text-xs font-semibold text-white">
                    <Check className="h-3 w-3" /> Active Theme
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-600 mb-4">
                Standard corporate microfinance & cooperative interface with green accent bars, sidebar navigation, and enterprise table listings.
              </p>

              {/* Wireframe Mockup */}
              <div className="rounded-xl border border-gray-200 bg-gray-100 p-3 mb-4 space-y-2">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-16 rounded bg-gray-300" />
                  <div className="h-3 w-8 rounded bg-green-500/80 ml-auto" />
                </div>
                <div className="grid grid-cols-4 gap-2">
                  <div className="h-10 rounded bg-white border border-gray-200 col-span-1" />
                  <div className="h-10 rounded bg-white border border-gray-200 col-span-3" />
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                setTheme('default')
              }}
              className={`w-full rounded-lg px-4 py-2.5 text-xs font-bold transition-colors ${
                theme === 'default'
                  ? 'bg-green-600 text-white shadow-sm'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {theme === 'default' ? 'Currently Selected' : 'Select Default UI'}
            </button>
          </div>

          {/* Paper Loan Ledger UI (Manila Paper Theme) Card */}
          <div
            onClick={() => setTheme('paper-loan-ledger')}
            className={`relative flex flex-col justify-between rounded-2xl border-2 p-5 cursor-pointer transition-all ${
              theme === 'paper-loan-ledger'
                ? 'border-[#8b7d6b] bg-[#faf4e4] shadow-xl ring-4 ring-[#8b7d6b]/30'
                : 'border-[#b5a793]/70 bg-[#faf4e4]/60 hover:border-[#8b7d6b] hover:shadow-md'
            }`}
          >
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-black uppercase tracking-wide text-[#2b2011]">
                  Paper Loan Ledger UI (Cebuano / Bisaya)
                </span>
                {theme === 'paper-loan-ledger' && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#3c2f1e] px-2.5 py-0.5 text-xs font-bold text-[#faf4e4] shadow">
                    <Check className="h-3 w-3 text-amber-400" /> Active UI Theme
                  </span>
                )}
              </div>
              <div className="mb-3">
                <span className="inline-block rounded bg-amber-700 px-2 py-0.5 text-[10px] font-extrabold uppercase tracking-wider text-white shadow-sm">
                  ★ Pixel-Perfect Clone · PC / Tablet / Mobile Phone
                </span>
              </div>
              <p className="text-xs text-[#5e4b33] mb-4">
                Authentic Philippine Manila Paper Ledger ("Talaan sa Utang"). Features physical folder tabs, PHP ₱50,000.00 credit limits, cursive Edna S. Santos signature, and 3 distinct device layouts.
              </p>

              {/* Manila Paper Mini Preview */}
              <div className="rounded-xl border-2 border-[#7a6d56] bg-[#efe3c8] p-3 mb-4 space-y-2 shadow-inner">
                <div className="flex items-center justify-between border-b border-[#8b7d6b] pb-1.5">
                  <span className="font-mono text-[10px] font-black text-[#2b2011] uppercase">
                    PANGUNA NGA TALAAN SA UTANG
                  </span>
                  <span className="font-mono text-[10px] font-bold text-[#1e150a]">₱50,000.00</span>
                </div>
                <div className="grid grid-cols-5 gap-1.5 pt-1">
                  <div className="rounded border border-[#8b7d6b] bg-[#faf4e4] px-1.5 py-1 text-[9px] font-bold text-[#3c2f1e] col-span-2">
                    📁 Mga HOA Empleyado
                  </div>
                  <div className="rounded border border-[#8b7d6b] bg-[#e6d6b5] px-1.5 py-1 text-[9px] font-mono text-[#1e150a] col-span-3 text-right">
                    REYNAN T. · ₱14,499.00
                  </div>
                </div>
                <div className="flex items-center justify-between pt-1">
                  <span className="text-[9px] font-black uppercase text-[#4e3b24]">Pirma: Edna S. Santos</span>
                  <span className="text-[9px] font-bold text-amber-900">PC · Tablet · Phone</span>
                </div>
              </div>

              {/* Device Viewport Preference */}
              {theme === 'paper-loan-ledger' && (
                <div className="mb-4 rounded-xl border border-[#8b7d6b]/50 bg-[#e6d6b5]/60 p-3">
                  <p className="text-[11px] font-bold uppercase text-[#3c2f1e] mb-2">
                    Preferred Device Viewport:
                  </p>
                  <div className="grid grid-cols-4 gap-1">
                    {(['auto', 'pc', 'tablet', 'phone'] as const).map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation()
                          setDeviceView(v)
                        }}
                        className={`rounded px-2 py-1 text-[10px] font-bold uppercase transition-colors ${
                          deviceView === v
                            ? 'bg-[#3c2f1e] text-white shadow-sm'
                            : 'bg-[#faf4e4] text-[#5e4b33] hover:bg-[#efe3c8]'
                        }`}
                      >
                        {v === 'auto' ? '⚡ Auto' : v === 'pc' ? '🖥️ PC' : v === 'tablet' ? '📱 Tablet' : '📲 Phone'}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  setTheme('paper-loan-ledger')
                }}
                className={`w-full rounded-lg px-4 py-2.5 text-xs font-black uppercase transition-all ${
                  theme === 'paper-loan-ledger'
                    ? 'bg-[#3c2f1e] text-[#faf4e4] shadow-md ring-2 ring-[#8b7d6b]'
                    : 'bg-[#e4d5b7] text-[#2b2011] hover:bg-[#d8c7a6]'
                }`}
              >
                {theme === 'paper-loan-ledger' ? 'Currently Selected Theme' : 'Select Paper Loan UI'}
              </button>
              <Link
                to="/paper-loan-ledger"
                className="flex w-full items-center justify-center gap-1.5 rounded-lg border-2 border-[#7a6d56] bg-[#faf4e4] px-4 py-2 text-xs font-black uppercase text-[#2b2011] hover:bg-[#efe3c8] shadow-sm transition-colors"
              >
                <span>Open Full Paper Loan UI & Device Preview</span>
                <ExternalLink className="h-3.5 w-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </Card>
    </div>
  )
}

